import React, { useEffect } from "react";

const STYLE_ID = "streaming-status-styles";

// Shared by the analysis-steps header and the BestX loading line so both read
// as the same "working on it" treatment.
const STYLES = `
.ss-dots {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 2px;
}
.ss-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: var(--ss-accent, #47a9f8);
  animation: ss-pulse 1.5s ease-in-out infinite;
}
.ss-dot:nth-child(2) {
  width: 3px;
  height: 3px;
  animation-delay: 0.45s;
}
@keyframes ss-pulse {
  0%, 100% { opacity: 0.3; }
  50% { opacity: 1; }
}

/* One highlight crosses the label left to right, then the label rests until the
   next pass. Gradient is 250% of the label, so at both ends of the travel the
   highlight sits off-screen and the loop's reset is invisible. */
.ss-shimmer {
  color: transparent;
  background: linear-gradient(
    90deg,
    rgba(232, 237, 244, 0.42) 0%,
    rgba(232, 237, 244, 0.42) 42%,
    rgba(232, 237, 244, 0.95) 50%,
    rgba(232, 237, 244, 0.42) 58%,
    rgba(232, 237, 244, 0.42) 100%
  );
  background-size: 250% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  animation: ss-wave 2s linear infinite;
}
@keyframes ss-wave {
  /* 90% puts the highlight just off the left edge, 10% just off the right. */
  0% { background-position: 90% 0; }
  60% { background-position: 10% 0; }
  100% { background-position: 10% 0; }
}

.ss-root {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  line-height: 1.5;
}

@media (prefers-reduced-motion: reduce) {
  .ss-dot { animation: none; opacity: 0.7; }
  .ss-shimmer { animation: none; }
}
`;

export const useStreamingStyles = () => {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
  }, []);
};

export const StreamingDots: React.FC = () => (
  <span className="ss-dots" aria-hidden="true">
    <span className="ss-dot" />
    <span className="ss-dot" />
  </span>
);

const StreamingStatus: React.FC<{ label: string; className?: string }> = ({
  label,
  className,
}) => {
  useStreamingStyles();

  return (
    <span
      className={className ? `ss-root ${className}` : "ss-root"}
      role="status"
      aria-live="polite"
    >
      <StreamingDots />
      <span className="ss-shimmer">{label}</span>
    </span>
  );
};

export default StreamingStatus;
